/**
 * Analyser ([D-289], 11-09-2026): kategorier, UID-stier og de gamle slugs.
 *
 * Delt mellem server og web, fordi tre steder ellers ville drifte fra
 * hinanden: registret (server), side-kataloget/authz (server), routeren og
 * sidebaren (web).
 *
 * KATEGORIERNE er en NAVIGATIONS-inddeling, ikke en adgangs-inddeling.
 * Undermenuen under Analyser viser kategorierne, og under hver kategori de
 * analyser brugeren har fået adgang til. Adgang gives fortsat PR. ANALYSE
 * ([D-215], fail-closed [D-141]): der findes intet grant på en kategori og
 * intet grant der åbner "alle analyser" — en kategori uden synlige analyser
 * findes ikke i menuen for den bruger.
 *
 * UID-STIERNE: en analyse bor på `/analyses/<32 hex-tegn>`, ikke på en slug
 * man kan gætte (Charlie 11-09-2026, samme model som kampagnernes public_id
 * [D-267]). UID'et er FROSSET i analysens kodefil sammen med resten af
 * casen og genereres med 16 tilfældige bytes ved oprettelsen. Det er ikke
 * adgangsværnet (det er grantet), men det gør, at en URL ikke afslører at
 * andre analyser findes, og at en delt adresse ikke kan "tælles videre".
 */

export const ANALYSIS_CATEGORIES = [
  { key: 'collabs', label: 'Samarbejder' },
  { key: 'consultants', label: 'Konsulenter' },
  { key: 'saas', label: 'SaaS' },
  { key: 'performance', label: 'Performance' },
  // [D-313]: analyser af sortimentet (billeder, tekster, produktdata).
  { key: 'products', label: 'Produkter' },
  // [D-324]: undersøgelser besvaret af rigtige mennesker (målgruppeanalysen
  // maj 2025). Svarene bor struktureret i survey_responses, så de kan slås op
  // af kommende agenter (SEO-tekster, SoMe, mails), ikke kun læses i analysen.
  { key: 'surveys', label: 'Spørgeskemaer' },
  { key: 'finance', label: 'Finans' },
] as const;

export type AnalysisCategory = (typeof ANALYSIS_CATEGORIES)[number]['key'];

export const ANALYSIS_CATEGORY_KEYS: readonly AnalysisCategory[] = ANALYSIS_CATEGORIES.map((c) => c.key);

export function analysisCategoryLabel(key: string): string {
  return ANALYSIS_CATEGORIES.find((c) => c.key === key)?.label ?? key;
}

/** Kategoriens plads i menuen (0 = først). Ukendt nøgle sorteres sidst. */
export function analysisCategoryOrder(key: string): number {
  const i = ANALYSIS_CATEGORIES.findIndex((c) => c.key === key);
  return i < 0 ? ANALYSIS_CATEGORIES.length : i;
}

/** Oversigtens sti — analysernes fælles forælder, men et SELVSTÆNDIGT grant. */
export const ANALYSES_OVERVIEW_PATH = '/analyses';

/** 32 hex-tegn (16 tilfældige bytes), præcis som kampagnernes public_id. */
export const ANALYSIS_UID_RE = /^[0-9a-f]{32}$/;

export function isAnalysisUid(value: string): boolean {
  return ANALYSIS_UID_RE.test(value);
}

/** Side-stien for én analyse. ÉT sted, så katalog, authz, router og sidebar ikke drifter. */
export function analysisPagePath(uid: string): string {
  return `${ANALYSES_OVERVIEW_PATH}/${uid}`;
}

/**
 * Slugs der har været i drift FØR UID-stierne (12-conventions §21: en slug
 * der har været i drift dør aldrig). Routeren redirecter dem til UID-stien,
 * og grants blev kopieret med `migrate:page-slug-grants` ved skiftet.
 *
 * ⚠ Kun de analyser der fandtes 11-09-2026 står her. Nye analyser får ALDRIG
 * en slug-sti, så listen vokser ikke.
 */
export const ANALYSIS_LEGACY_SLUGS: Readonly<Record<string, string>> = {
  'bonzer-seo': 'b39981f163c95e4ef80cee7ac8e2efbc',
  indretningskonsulent: 'c29cc5a7d2083735ad04d281ea2b3f89',
  'plan-sep-dec-2026': '5c3e78c31a14a20f8125d2b1703d3ff6',
  'loyalty-key-cashback': '3847bb3f08c3cf0cc5762f627433ec34',
  prisjustering: '684b661026d3f74f4ec4e0b1ae25c49b',
};

/**
 * Analyser der er FLYTTET ind på en kampagnes side ([D-355], Charlie
 * 01-10-2026): uid → kampagnens public_id. Black November 2026 var en
 * selvstændig analyse (kampagneplanen, [D-304]); nu står planen, tidslinjen og
 * prognosen på kampagnens egen side. Adressen dør aldrig (§21): routeren
 * sender den direkte videre til kampagnen, og analysen findes ikke længere i
 * listen, menuen eller Siderettigheder.
 */
export const ANALYSES_MOVED_TO_CAMPAIGNS: Readonly<Record<string, string>> = {
  '9e8e8958c9ba080b6c1bf354664f7a32': '50835c1ce913aee43b2b7d881ee4d98a',
};

/** Slug ELLER uid → uid (null hvis ukendt slug og ikke et uid). */
export function resolveAnalysisUid(ref: string): string | null {
  if (isAnalysisUid(ref)) return ref;
  return ANALYSIS_LEGACY_SLUGS[ref] ?? null;
}

/** Øvre grænse på en titel man selv giver en analyse ([D-289]). */
export const ANALYSIS_TITLE_MAX = 80;
